import React, { useEffect, useState } from 'react';
import { useTranslations, useLocale } from 'use-intl';
import { ChevronUp, ChevronDown, Plus, Trash2, X, Edit2, Info } from 'react-feather';
import {
	useRiskNotificationPhonesStore,
	RiskNotificationPhone,
	isValidE164,
	normalizeToE164
} from '@/pages/SupportClients/useRiskNotificationPhones';
import Button from '@/stories/Library/Components/Button/Button';
import InputField from '@/stories/Library/Components/InputField/InputField';
import Modal from '@/stories/Library/Components/Modal/Modal';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { getLanguageFromPath } from '@/utils/utils';
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell
} from '@/stories/Shadcn/Table/Table';

interface RiskNotificationPhonesSectionProps {
	organizationId: string | null;
	embeddedInTab?: boolean;
}

const RiskNotificationPhonesSection: React.FC<RiskNotificationPhonesSectionProps> = ({
	organizationId,
	embeddedInTab = false
}) => {
	const t = useTranslations('SupportClients');
	const locale = useLocale();
	const language = getLanguageFromPath(window.location.pathname) || locale;

	const {
		phones,
		isLoading,
		error,
		loadPhones,
		insertPhone,
		updatePhone,
		toggleActive,
		deletePhone
	} = useRiskNotificationPhonesStore();

	const [isExpanded, setIsExpanded] = useState(true);
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [newPhone, setNewPhone] = useState('');
	const [newName, setNewName] = useState('');
	const [deletingPhone, setDeletingPhone] = useState<RiskNotificationPhone | null>(null);
	const [editingPhone, setEditingPhone] = useState<RiskNotificationPhone | null>(null);
	const [editingName, setEditingName] = useState('');
	const [editingPhoneNumber, setEditingPhoneNumber] = useState('');
	const [editingActive, setEditingActive] = useState(true);

	useEffect(() => {
		if (organizationId) {
			loadPhones(organizationId);
		}
	}, [organizationId, loadPhones]);

	const formatDate = (dateString: string | null) => {
		if (!dateString) return 'N/A';
		const localeCode = language === 'es' ? 'es-MX' : 'en-US';
		return new Date(dateString).toLocaleDateString(localeCode, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const handleOpenAddModal = () => {
		setNewPhone('');
		setNewName('');
		setIsAddModalOpen(true);
	};

	const handleCloseAddModal = () => {
		setIsAddModalOpen(false);
		setNewPhone('');
		setNewName('');
	};

	const handleOpenEditModal = (phone: RiskNotificationPhone) => {
		setEditingPhone(phone);
		setEditingName(phone.name || '');
		setEditingPhoneNumber(phone.phone_number);
		setEditingActive(phone.is_active);
		setIsEditModalOpen(true);
	};

	const handleCloseEditModal = () => {
		setIsEditModalOpen(false);
		setEditingPhone(null);
		setEditingName('');
		setEditingPhoneNumber('');
		setEditingActive(true);
	};

	const handleOpenDeleteModal = (phone: RiskNotificationPhone) => {
		setDeletingPhone(phone);
		setIsDeleteModalOpen(true);
	};

	const handleCloseDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setDeletingPhone(null);
	};

	const handleAddPhone = async () => {
		if (!organizationId || !newPhone.trim()) return;

		const normalized = normalizeToE164(newPhone.trim());
		if (!isValidE164(normalized)) {
			return;
		}

		const success = await insertPhone(organizationId, normalized, newName.trim() || null);
		if (success) {
			handleCloseAddModal();
		}
	};

	const handleSaveEdit = async () => {
		if (!organizationId || !editingPhone) return;

		const normalizedPhone = normalizeToE164(editingPhoneNumber.trim());
		if (!isValidE164(normalizedPhone)) {
			return;
		}

		const success = await updatePhone(editingPhone.id, organizationId, {
			name: editingName.trim() || null,
			phone_number: normalizedPhone,
			is_active: editingActive
		});
		if (success) {
			handleCloseEditModal();
		}
	};

	const handleDelete = async () => {
		if (!organizationId || !deletingPhone) return;

		const success = await deletePhone(deletingPhone.id, organizationId);
		if (success) {
			handleCloseDeleteModal();
		}
	};

	const handleToggleActive = async (phone: RiskNotificationPhone) => {
		if (!organizationId) return;
		await toggleActive(phone.id, organizationId, !phone.is_active);
	};

	const renderContent = () => (
		<div className="mt-4">
			<div className="flex gap-3 p-4 mb-4 rounded-lg bg-blue-50 border border-blue-100">
				<Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
				<p className="text-sm text-blue-900">{t('RiskNotificationPhonesDisclaimer')}</p>
			</div>
			{isLoading ? (
				<p className="text-gray-500 text-center py-6">{t('LoadingVariables')}</p>
			) : phones.length === 0 ? (
				<p className="text-gray-500 text-center py-6">{t('NoNotificationPhones')}</p>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{t('PhoneContactName')}</TableHead>
							<TableHead>{t('PhoneNumber')}</TableHead>
							<TableHead>{t('Active')}</TableHead>
							<TableHead>{t('CreatedAt')}</TableHead>
							<TableHead>{t('Actions')}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{phones.map((phone) => (
							<TableRow key={phone.id}>
								<TableCell>
									<span className="font-medium">{phone.name || '—'}</span>
									<button
										onClick={() => handleOpenEditModal(phone)}
										className="ml-2 p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700 inline-flex"
									>
										<Edit2 size={14} />
									</button>
								</TableCell>
								<TableCell className="font-medium">{phone.phone_number}</TableCell>
								<TableCell>
									<button
										onClick={() => handleToggleActive(phone)}
										className={`px-2 py-1 rounded-full text-xs font-medium ${
											phone.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
										}`}
									>
										{phone.is_active ? t('Active') : t('Inactive')}
									</button>
								</TableCell>
								<TableCell className="text-gray-500">{formatDate(phone.created_at)}</TableCell>
								<TableCell>
									<button
										onClick={() => handleOpenDeleteModal(phone)}
										className="p-1 hover:bg-red-50 rounded text-gray-600 hover:text-red-600"
									>
										<Trash2 size={16} />
									</button>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</div>
	);

	return (
		<div
			className={`bg-white rounded-lg border border-gray-200 p-6 ${embeddedInTab ? '' : 'mb-6'}`}
		>
			{embeddedInTab ? (
				<>
					<div className="flex justify-end mb-4">
						<Button
							label={t('AddPhone')}
							onClick={handleOpenAddModal}
							startIcon={<Plus size={16} />}
							color="primary"
							size="small"
						/>
					</div>
					{renderContent()}
				</>
			) : (
				<>
					<div
						className="flex items-center justify-between cursor-pointer"
						onClick={() => setIsExpanded(!isExpanded)}
					>
						<h2 className="text-lg font-semibold">{t('RiskNotificationPhones')}</h2>
						<div className="flex items-center gap-2">
							{isExpanded && (
								<div onClick={(e) => e.stopPropagation()}>
									<Button
										label={t('AddPhone')}
										onClick={handleOpenAddModal}
										startIcon={<Plus size={16} />}
										color="primary"
										size="small"
									/>
								</div>
							)}
							{isExpanded ? (
								<ChevronUp size={20} className="text-gray-500" />
							) : (
								<ChevronDown size={20} className="text-gray-500" />
							)}
						</div>
					</div>
					{isExpanded && renderContent()}
				</>
			)}

			<Modal isOpen={isAddModalOpen} closeable onClose={handleCloseAddModal} maxWidth={500}>
				<div className="flex flex-col gap-6">
					<Typography type="display" size="sm">
						{t('AddPhone')}
					</Typography>

					<InputField
						label={t('PhoneContactName')}
						type="text"
						value={newName}
						onChange={(e) => setNewName(e.target.value)}
						placeholder={t('PhoneContactNamePlaceholder')}
						size="default"
					/>
					<InputField
						label={t('PhoneNumber')}
						type="text"
						value={newPhone}
						onChange={(e) => setNewPhone(e.target.value)}
						placeholder="+525512345678"
						size="default"
					/>
					<p className="text-sm text-gray-500">{t('PhoneE164Hint')}</p>

					{error && <p className="text-red-500 text-sm">{error}</p>}

					<div className="flex gap-2 pt-2">
						<Button
							label={t('Cancel')}
							onClick={handleCloseAddModal}
							startIcon={<X size={16} />}
							color="secondary"
							size="medium"
							outlined
						/>
						<Button
							label={t('Save')}
							onClick={handleAddPhone}
							color="primary"
							size="medium"
							disabled={!newPhone.trim() || !isValidE164(normalizeToE164(newPhone.trim()))}
						/>
					</div>
				</div>
			</Modal>

			<Modal isOpen={isEditModalOpen} closeable onClose={handleCloseEditModal} maxWidth={500}>
				<div className="flex flex-col gap-6">
					<Typography type="display" size="sm">
						{t('EditPhone')}
					</Typography>

					<InputField
						label={t('PhoneContactName')}
						type="text"
						value={editingName}
						onChange={(e) => setEditingName(e.target.value)}
						placeholder={t('PhoneContactNamePlaceholder')}
						size="default"
					/>
					<InputField
						label={t('PhoneNumber')}
						type="text"
						value={editingPhoneNumber}
						onChange={(e) => setEditingPhoneNumber(e.target.value)}
						placeholder="+525512345678"
						size="default"
					/>
					<p className="text-sm text-gray-500">{t('PhoneE164Hint')}</p>

					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={editingActive}
							onChange={(e) => setEditingActive(e.target.checked)}
							className="rounded border-gray-300"
						/>
						<span className="text-sm font-medium">{t('Active')}</span>
					</label>

					{error && <p className="text-red-500 text-sm">{error}</p>}

					<div className="flex gap-2 pt-2">
						<Button
							label={t('Cancel')}
							onClick={handleCloseEditModal}
							startIcon={<X size={16} />}
							color="secondary"
							size="medium"
							outlined
						/>
						<Button
							label={t('Save')}
							onClick={handleSaveEdit}
							color="primary"
							size="medium"
							disabled={
								!editingPhoneNumber.trim() ||
								!isValidE164(normalizeToE164(editingPhoneNumber.trim()))
							}
						/>
					</div>
				</div>
			</Modal>

			<Modal isOpen={isDeleteModalOpen} closeable onClose={handleCloseDeleteModal} maxWidth={450}>
				<div className="flex flex-col gap-6">
					<Typography type="display" size="sm">
						{t('ConfirmDeletePhone')}
					</Typography>
					{deletingPhone && (
						<p className="text-gray-600">
							{deletingPhone.name ? `${deletingPhone.name} — ` : ''}
							{deletingPhone.phone_number}
						</p>
					)}
					<div className="flex gap-2 pt-2">
						<Button
							label={t('Cancel')}
							onClick={handleCloseDeleteModal}
							startIcon={<X size={16} />}
							color="secondary"
							size="medium"
							outlined
						/>
						<Button label={t('Delete')} onClick={handleDelete} color="primary" size="medium" />
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default RiskNotificationPhonesSection;
